'use client';

import { useState, useCallback } from 'react';
import { Star, MapPin } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { Card } from '@/shared/ui';
import type { Hotel, MapHotel, PopularLocation } from '../types/hotels.types';
import {
  PRICE_RANGES,
  INDIA_OUTLINE_PATH,
  projectToSvg,
  radiusToSvgPixels,
} from './hotels.data';

/* -------------------------------------------------------------------------- */
/*  Pin color helper                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Pin colour by price band, or the neutral band when there is no price.
 *
 * `price` is null for a property with no room types (D-26/D-27). Falling into
 * the cheapest band would colour an unpriced hotel as a bargain.
 */
function getPinColor(price: number | null | undefined): string {
  if (typeof price !== 'number' || !Number.isFinite(price)) return '#9CA3AF';

  // Was `const { t } = useTranslation();` here — doubly wrong: a hook in a
  // plain helper, AND after the early return above, so it ran only for some
  // inputs. `t` was never used; every branch returns a hex literal.
  for (const range of PRICE_RANGES) {
    if (price >= range.min && price <= range.max) return range.color;
  }
  return '#C8913A';
}

/* -------------------------------------------------------------------------- */
/*  Deterministic INR formatter                                               */
/* -------------------------------------------------------------------------- */

/** A rupee amount, or "Price on request" when there is none (D-26/D-27). */
function formatINR(n: number | null | undefined): string {
  // A rupee amount, or an honest gap. Formatting null yields "0" and undefined
  // yields "NaN", so a property with no rates rendered as Rs 0 — read as free,
  // not as "not yet priced" (D-26/D-27).
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) {
    return 'Price on request';
  }
  const s = String(Math.round(n));
  if (s.length <= 3) return '₹' + s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  return '₹' + rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

/* -------------------------------------------------------------------------- */
/*  India Map Background                                                       */
/* -------------------------------------------------------------------------- */

function IndiaMapBackground() {
  return (
    <g id="hotels-map-india-bg" opacity="0.2">
      {/* India outline */}
      <path
        d={INDIA_OUTLINE_PATH}
        style={{ fill: 'rgb(var(--hms-border))', stroke: 'rgb(var(--hms-textDim))' }}
        strokeWidth="1.5"
        opacity="0.6"
      />
      {/* Grid lines for scale reference */}
      {[10, 15, 20, 25, 30, 35].map((lat) => {
        const { y } = projectToSvg(lat, 68);
        return (
          <line
            key={`lat-${lat}`}
            x1="0" y1={y} x2="1000" y2={y}
            style={{ stroke: 'rgb(var(--hms-border))' }} strokeWidth="0.3" strokeDasharray="4,8"
          />
        );
      })}
      {[70, 75, 80, 85, 90, 95].map((lng) => {
        const { x } = projectToSvg(6, lng);
        return (
          <line
            key={`lng-${lng}`}
            x1={x} y1="0" x2={x} y2="562"
            style={{ stroke: 'rgb(var(--hms-border))' }} strokeWidth="0.3" strokeDasharray="4,8"
          />
        );
      })}
    </g>
  );
}

/* -------------------------------------------------------------------------- */
/*  Radius Circle                                                              */
/* -------------------------------------------------------------------------- */

function RadiusCircle({ lat, lng, radiusKm }: { lat: number; lng: number; radiusKm: number }) {
  const center = projectToSvg(lat, lng);
  const r = radiusToSvgPixels(radiusKm);

  return (
    <g id="hotels-map-radius-circle">
      {/* Radius fill */}
      <circle
        cx={center.x} cy={center.y} r={r}
        fill="#0D9E99" opacity="0.08"
        stroke="#0D9E99" strokeWidth="1.5" strokeDasharray="6,4"
      />
      {/* Center dot */}
      <circle cx={center.x} cy={center.y} r="4" fill="#0D9E99" opacity="0.8" />
      {/* Radius label */}
      <text
        x={center.x} y={center.y - r - 6}
        textAnchor="middle"
        fill="#0D9E99"
        fontSize="10"
        fontWeight="600"
        opacity="0.9"
      >
        {radiusKm} km radius
      </text>
    </g>
  );
}

/* -------------------------------------------------------------------------- */
/*  Hotel Pin SVG                                                             */
/* -------------------------------------------------------------------------- */

function HotelPin({
  hotel,
  isActive,
  onClick,
}: {
  hotel: MapHotel;
  isActive: boolean;
  onClick: () => void;
}) {
  const color = getPinColor(hotel.price);
  const scale = isActive ? 1.25 : 1;
  const pos = projectToSvg(hotel.latitude, hotel.longitude);

  return (
    <g
      id={`hotels-map-pin-${hotel.id}`}
      transform={`translate(${pos.x}, ${pos.y})`}
      onClick={onClick}
      className="cursor-pointer"
    >
      {/* Pulse ring for active */}
      {isActive && (
        <circle cx="0" cy="0" r="14" fill={color} opacity="0.2" />
      )}
      {/* Pin body */}
      <g transform={`scale(${scale})`} style={{ transformOrigin: '0 0' }}>
        <path
          d="M0,-12 C-6.6,-12 -12,-6.6 -12,0 C-12,8 0,16 0,16 C0,16 12,8 12,0 C12,-6.6 6.6,-12 0,-12Z"
          fill={color}
        />
        <circle cx="0" cy="-1" r="4" style={{ fill: 'rgb(var(--hms-bg))' }} />
      </g>
      {/* City label */}
      <text
        x="0" y="22"
        textAnchor="middle"
        style={{ fill: 'rgb(var(--hms-textSub))' }}
        fontSize="9"
        fontWeight="500"
      >
        {hotel.city}
      </text>
    </g>
  );
}

/* -------------------------------------------------------------------------- */
/*  Floating Info Card                                                        */
/* -------------------------------------------------------------------------- */

function InfoCard({ hotel, onClose }: { hotel: MapHotel; onClose: () => void }) {
  const pos = projectToSvg(hotel.latitude, hotel.longitude);
  const pctX = (pos.x / 1000) * 100;
  const pctY = (pos.y / 562) * 100;

  return (
    <div
      id={`hotels-map-info-${hotel.id}`}
      className={cn(
        'absolute z-20 w-48 sm:w-56 rounded-xl border border-hms-border bg-hms-card p-3 sm:p-4 shadow-xl',
        'transition-opacity duration-200',
      )}
      style={{
        left: `${pctX}%`,
        top: `${pctY}%`,
        transform: 'translate(-50%, -110%)',
      }}
    >
      {/* Close */}
      <button
        id={`hotels-map-info-close-${hotel.id}`}
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded p-0.5 text-hms-textDim hover:text-hms-text"
        aria-label="Close info card"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Hotel Name */}
      <h4 className="pr-5 text-xs font-semibold text-hms-text truncate">{hotel.name}</h4>

      {/* City + Coordinates */}
      <div className="mt-1 flex items-center gap-1 text-[10px] text-hms-textSub">
        <MapPin className="h-3 w-3 text-hms-teal" />
        {hotel.city}
      </div>
      <div className="mt-0.5 text-[9px] text-hms-textDim font-mono">
        {hotel.latitude.toFixed(4)}&deg;N, {hotel.longitude.toFixed(4)}&deg;E
      </div>

      {/* Rating & Price */}
      <div className="mt-2.5 flex items-center justify-between">
        <span className="inline-flex items-center gap-1 rounded-md bg-hms-gold/10 px-2 py-0.5 text-[11px] font-bold text-hms-gold">
          <Star className="h-3 w-3 fill-hms-gold text-hms-gold" />
          {hotel.rating}
        </span>
        <span className="text-xs font-semibold text-hms-text">
          {formatINR(hotel.price)}
          <span className="ml-0.5 text-[9px] font-normal text-hms-textDim">/night</span>
        </span>
      </div>

      {/* View Details */}
      <a
        id={`hotels-map-details-${hotel.id}`}
        href={`/hotels/${hotel.id}`}
        className={cn(
          'mt-3 flex h-8 w-full items-center justify-center rounded-lg',
          'border border-hms-gold/30 text-[11px] font-semibold text-hms-gold',
          'transition-all hover:bg-hms-gold/10',
        )}
      >
        View Details
      </a>

      {/* Arrow */}
      <div
        className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r border-hms-border bg-hms-card"
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  HotelMapView                                                              */
/* -------------------------------------------------------------------------- */

interface HotelMapViewProps {
  hotels?: Hotel[];
  searchLocation?: PopularLocation | null;
  radiusKm?: number;
}

export function HotelMapView({ hotels = [], searchLocation, radiusKm = 0 }: HotelMapViewProps) {
  const [activeHotelId, setActiveHotelId] = useState<string | null>(null);

  const handlePinClick = useCallback((hotelId: string) => {
    setActiveHotelId((prev) => (prev === hotelId ? null : hotelId));
  }, []);

  const handleInfoClose = useCallback(() => {
    setActiveHotelId(null);
  }, []);

  /* Convert Hotel[] to MapHotel[] for pins — skip hotels without coordinates */
  const mapHotels: MapHotel[] = hotels
    .filter((h) => h.latitude && h.longitude)
    .map((h) => ({ id: h.id, name: h.name, city: h.location, price: h.price, rating: h.rating, latitude: h.latitude, longitude: h.longitude }));

  const activeHotel = mapHotels.find((h) => h.id === activeHotelId) ?? null;

  return (
    <div id="hotels-map-container" className="space-y-4">
      <Card padding="none" className="relative overflow-hidden">
        {/* SVG Map */}
        <div className="relative" style={{ paddingBottom: '56.25%' }}>
          <svg
            id="hotels-map-svg"
            viewBox="0 0 1000 562"
            className="absolute inset-0 h-full w-full"
            preserveAspectRatio="xMidYMid slice"
          >
            {/* Background */}
            <rect width="1000" height="562" style={{ fill: 'rgb(var(--hms-surface))' }} />
            <IndiaMapBackground />

            {/* Radius circle */}
            {searchLocation && radiusKm > 0 && (
              <RadiusCircle
                lat={searchLocation.latitude}
                lng={searchLocation.longitude}
                radiusKm={radiusKm}
              />
            )}

            {/* Hotel Pins */}
            {mapHotels.map((hotel) => (
              <HotelPin
                key={hotel.id}
                hotel={hotel}
                isActive={activeHotelId === hotel.id}
                onClick={() => handlePinClick(hotel.id)}
              />
            ))}
          </svg>

          {/* Floating info cards rendered as HTML overlay */}
          {activeHotel && (
            <InfoCard hotel={activeHotel} onClose={handleInfoClose} />
          )}
        </div>
      </Card>

      {/* Legend */}
      <div
        id="hotels-map-legend"
        className="flex flex-wrap items-center gap-4 rounded-lg border border-hms-border bg-hms-card px-4 py-3"
      >
        <span className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
          Price Range
        </span>
        {PRICE_RANGES.map((range) => (
          <div key={range.label} className="flex items-center gap-2">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24">
              <path
                d="M12,2 C8.14,2 5,5.14 5,9 C5,14.25 12,22 12,22 C12,22 19,14.25 19,9 C19,5.14 15.86,2 12,2Z"
                fill={range.color}
              />
              <circle cx="12" cy="9" r="3" style={{ fill: 'rgb(var(--hms-bg))' }} />
            </svg>
            <span className="text-[11px] text-hms-textSub">{range.label}</span>
          </div>
        ))}

        {searchLocation && radiusKm > 0 && (
          <>
            <span className="mx-2 h-4 w-px bg-hms-border" />
            <div className="flex items-center gap-2">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="none" stroke="#0D9E99" strokeWidth="2" strokeDasharray="3,2" />
                <circle cx="12" cy="12" r="3" fill="#0D9E99" />
              </svg>
              <span className="text-[11px] text-hms-teal font-medium">
                {searchLocation.name} &middot; {radiusKm} km radius
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
